import React, { useState } from 'react';
import { FaCheckCircle, FaExclamationTriangle } from 'react-icons/fa';
import {
  GRADE_META,
  LISTABLE_GRADES,
  gradeToCondition,
} from '../../utils/usedPartGrades';

const GRADE_COLORS = {
  A: '#059669',
  B: '#2563eb',
  C: '#d97706',
};

/**
 * Radio card selector for seller-declared used-part grades.
 * "Not Eligible" is shown but cannot be selected.
 */
export default function UsedGradeCardSelector({ value, onChange, groupName = 'used-grade' }) {
  const [ineligibleHover, setIneligibleHover] = useState(false);
  const ineligible = GRADE_META.NOT_ELIGIBLE;

  const handleSelect = (grade) => {
    const meta = GRADE_META[grade];
    if (!meta?.selectable) return;
    onChange({
      usedGrade: grade,
      condition: gradeToCondition(grade),
    });
  };

  return (
    <div className="space-y-3">
      <p className="text-xs text-gray-600">
        Grade is seller-declared at listing time and must be backed by photo evidence. Sparepe does
        not physically inspect parts.
      </p>
      <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
        {LISTABLE_GRADES.map((grade) => {
          const meta = GRADE_META[grade];
          const isSelected = value === grade;
          const brandColor = GRADE_COLORS[grade];
          const inputId = `${groupName}-${grade}`;

          return (
            <div key={grade} className="relative">
              <input
                type="radio"
                id={inputId}
                name={groupName}
                value={grade}
                checked={isSelected}
                onChange={() => handleSelect(grade)}
                className="peer sr-only"
              />
              <label
                htmlFor={inputId}
                className="block w-full cursor-pointer select-none rounded-xl border-2 p-4 transition-all active:scale-[0.99]"
                style={
                  isSelected
                    ? {
                        borderColor: brandColor,
                        backgroundColor: `${brandColor}14`,
                        boxShadow: `0 4px 14px ${brandColor}33`,
                      }
                    : {
                        borderColor: '#e5e7eb',
                        backgroundColor: '#ffffff',
                      }
                }
              >
                {isSelected && (
                  <span
                    className="absolute right-3 top-3 flex h-6 w-6 items-center justify-center rounded-full text-white"
                    style={{ backgroundColor: brandColor }}
                  >
                    <FaCheckCircle className="text-xs" />
                  </span>
                )}
                <span className="flex items-start gap-3 pr-8">
                  <span
                    className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg text-lg font-bold text-white shadow-sm"
                    style={{ backgroundColor: brandColor }}
                  >
                    {grade}
                  </span>
                  <span className="min-w-0 flex-1">
                    <span className="block text-sm font-semibold text-gray-900">{meta.label}</span>
                    <span className="mt-1 block text-xs leading-relaxed text-gray-600">
                      {meta.condition}
                    </span>
                    <span className="mt-2 flex flex-wrap gap-2 text-[10px] font-medium uppercase tracking-wide text-gray-500">
                      <span className="rounded-full bg-gray-100 px-2 py-0.5">
                        Min {meta.minPhotos} photos
                      </span>
                      {meta.kmCap && (
                        <span className="rounded-full bg-gray-100 px-2 py-0.5">{meta.kmCap}</span>
                      )}
                      {meta.commissionPct && (
                        <span className="rounded-full bg-gray-100 px-2 py-0.5">
                          Commission {meta.commissionPct}
                        </span>
                      )}
                    </span>
                    {meta.photoRequirements && (
                      <span className="mt-1.5 block text-[11px] text-gray-500">
                        Photos: {meta.photoRequirements}
                      </span>
                    )}
                  </span>
                </span>
              </label>
            </div>
          );
        })}

        {/* Not Eligible — non-selectable */}
        <div
          className="relative md:col-span-2"
          onMouseEnter={() => setIneligibleHover(true)}
          onMouseLeave={() => setIneligibleHover(false)}
        >
          <div
            className="rounded-xl border-2 border-dashed border-gray-300 bg-gray-50 p-4 opacity-70 cursor-not-allowed select-none"
            aria-disabled="true"
            title={ineligible.tooltip}
          >
            <span className="flex items-start gap-3">
              <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-gray-300 text-gray-500">
                <FaExclamationTriangle className="text-lg" />
              </span>
              <span className="min-w-0 flex-1">
                <span className="block text-sm font-semibold text-gray-500">{ineligible.label}</span>
                <span className="mt-1 block text-xs leading-relaxed text-gray-400">
                  {ineligible.condition}
                </span>
              </span>
            </span>
          </div>
          {ineligibleHover && (
            <div
              role="tooltip"
              className="absolute left-1/2 top-full z-10 mt-2 -translate-x-1/2 whitespace-nowrap rounded-lg bg-gray-900 px-3 py-1.5 text-xs text-white shadow-lg"
            >
              {ineligible.tooltip}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
